import { FloatingTree } from '@floating-ui/react'
import { hasBlockInteraction, useContainerBlockContext, useCustomViewBlockContext } from '@lighthouse/block'
import { ContainerBlock } from '@lighthouse/block/src/blocks'
import type { FloatBlockAbstract, ResolvedContainerControlSwitchPanelParams } from '@lighthouse/core'
import { BlockType, type ContainerBlockAbstract, EVENT_TYPE } from '@lighthouse/core'
import type { FlowLayoutNode } from '@lighthouse/shared'
import {
    calculateNextPanelId,
    checkActionEvent,
    CONTAINER_PANEL_ACTION_TYPES,
    getCurrentBlockChildren,
    getCursorStyle,
    getRecordScopedListenerIds,
    getWithScopeId,
    getWithScopeIdAndRecordIdAndColumnId,
    MultipleFloatBoxProvider,
    readCustomViewStack,
    useActionRunningLoadings,
    useApplicationContext,
    useAtomData,
    useRegisterBlockListener,
    useRegisterBlockListeners,
    useUnifiedTap
} from '@lighthouse/shared'
import { useAtomCallback } from 'jotai/utils'
import { reduce } from 'rambda'
import React, { startTransition, useCallback, useEffect, useMemo } from 'react'
import { useMount } from 'react-use'

import { setContainerRuntimeStateAtom } from '@/atoms/page/action'
import { containerRuntimeStateAtomFamily } from '@/atoms/page/state'
import { useCurrentStackIdContext } from '@/contexts/PageContext'
import { useActionTrigger } from '@/hooks/useActionTrigger'
import { useFloatBlock } from '@/hooks/useFloatBlock'

interface ContainerControllerProps extends React.ComponentPropsWithoutRef<'div'> {
    blockData: ContainerBlockAbstract
    node: FlowLayoutNode
    disabled?: boolean
    extra?: React.ReactNode
    onBlockChange?: (values: ContainerBlockAbstract, origin: ContainerBlockAbstract) => Promise<void> | void
}

const ContainerController = ({ blockData, onBlockChange, style, ...props }: ContainerControllerProps) => {
    const { config, id } = blockData
    const { eventList, shown, floatPointerList, cursor, viewList, breakPoint } = config
    const { handleActionListTrigger } = useActionTrigger(id)
    const { loadings, handleActionTriggerWithLoading } = useActionRunningLoadings()
    const stackId = useCurrentStackIdContext()
    const { scope } = useContainerBlockContext()
    const customViewContext = useCustomViewBlockContext()
    const { record, editable } = customViewContext
    const uniqueId = useMemo(() => getWithScopeIdAndRecordIdAndColumnId(id, scope, record?.id), [id, scope, record?.id])

    // 基础状态key，用于状态存储
    const baseStateKey = useMemo(() => getWithScopeId(id, scope), [id, scope])
    // 自动循环定时器引用
    const timerIdRef = React.useRef<NodeJS.Timer | null>(null)
    // 上次调用时间记录，用于防抖
    const lastCallTimeRef = React.useRef<number>(0)
    // 防抖时间窗口（毫秒）- 过滤鼠标抖动导致的快速重复触发
    const DEBOUNCE_TIME = 500
    // 当前记录的ID，用于状态隔离
    const recordId = record?.id

    const containerState = useAtomData(containerRuntimeStateAtomFamily({ stackId, containerId: baseStateKey }))
    const currentView = useMemo(() => {
        if (!containerState) {
            return
        }
        // 如果在自定义视图内且有recordId，使用recordId隔离的状态
        if (recordId) {
            return containerState.recordStates?.[recordId]?.currentView || containerState.currentView
        }
        // 否则使用全局状态
        return containerState.currentView
    }, [containerState, recordId])
    const defaultView = useMemo(() => breakPoint.panel?.defaultPanel || viewList?.[0]?.id, [breakPoint.panel?.defaultPanel, viewList])

    const activeView = currentView || defaultView

    // 不在创建阶段写入 runtime state，读取时统一使用回退链处理。

    const handleViewChange = useAtomCallback<void, [string, number | undefined]>((get, set, switchPanelId, nthPanel) => {
        if (!switchPanelId) {
            return
        }

        const latestContainerState = get(containerRuntimeStateAtomFamily({ stackId, containerId: baseStateKey }))
        const latestCurrentView = recordId
            ? latestContainerState?.recordStates?.[recordId]?.currentView || latestContainerState?.currentView
            : latestContainerState?.currentView
        const latestActiveView = latestCurrentView || defaultView

        const targetPanelId = calculateNextPanelId(switchPanelId, latestActiveView, viewList, nthPanel)

        if (!targetPanelId) {
            return
        }

        set(setContainerRuntimeStateAtom, {
            stackId,
            containerId: baseStateKey,
            currentView: targetPanelId,
            recordId,
            updateGlobal: editable
        })
    })

    // 处理全局级别的面板切换（外部触发，影响所有记录）
    const handleGlobalViewChange = useAtomCallback<void, [string, number | undefined]>((get, set, switchPanelId, nthPanel) => {
        if (!switchPanelId) {
            return
        }

        const currentContainerState = get(containerRuntimeStateAtomFamily({ stackId, containerId: baseStateKey }))
        const currentPanelId = currentContainerState?.currentView || defaultView
        const targetPanelId = calculateNextPanelId(switchPanelId, currentPanelId, viewList, nthPanel)

        if (!targetPanelId) {
            return
        }

        set(setContainerRuntimeStateAtom, {
            stackId,
            containerId: baseStateKey,
            currentView: targetPanelId,
            updateAllRecords: true
        })
    })

    // 停止自动循环
    const stopAutoLoop = useCallback(() => {
        if (timerIdRef.current !== null) {
            clearInterval(timerIdRef.current)
            timerIdRef.current = null
        }
    }, [])

    // 启动自动循环
    const startAutoLoop = useCallback(
        (switchPanelId: string, interval: number, fn: (panelId: string) => void) => {
            const now = Date.now()
            const timeSinceLastCall = now - lastCallTimeRef.current

            // 如果距离上次调用不到防抖时间，忽略这次调用（过滤鼠标抖动）
            if (timeSinceLastCall < DEBOUNCE_TIME) {
                return
            }

            lastCallTimeRef.current = now
            stopAutoLoop() // 清理旧的定时器

            timerIdRef.current = setInterval(() => {
                startTransition(() => {
                    fn(switchPanelId)
                })
            }, interval)
        },
        [stopAutoLoop]
    )

    // 组件卸载时清理定时器
    useEffect(() => {
        return () => {
            stopAutoLoop()
        }
    }, [stopAutoLoop])

    const recordListenerIds = useMemo(() => {
        const scopedIds = getRecordScopedListenerIds(id, scope, readCustomViewStack(customViewContext))
        return scopedIds.length > 0 ? scopedIds : [baseStateKey]
    }, [baseStateKey, customViewContext, id, scope])

    useRegisterBlockListeners(recordListenerIds, 'container', {
        switchPanel: (payload?: ResolvedContainerControlSwitchPanelParams) => {
            if (!payload) {
                return
            }

            const { switchPanelId, autoForwardLoop, loopInterval = 1000, nthPanel } = payload

            // 仅当明确要求开启自动循环时才配置
            if (autoForwardLoop && CONTAINER_PANEL_ACTION_TYPES.includes(switchPanelId)) {
                startAutoLoop(switchPanelId, loopInterval, panelId => handleViewChange(panelId, undefined))
            } else {
                handleViewChange(switchPanelId, nthPanel)
            }
        },
        open: () => {
            const newBlockData = { ...blockData, config: { ...config, shown: true } }
            onBlockChange?.(newBlockData, blockData)
        },
        close: () => {
            const newBlockData = { ...blockData, config: { ...config, shown: false } }
            onBlockChange?.(newBlockData, blockData)
        }
    })

    // 如果在自定义视图内，额外注册全局级别的监听器（外部触发，影响所有记录）
    useRegisterBlockListener(
        recordId ? baseStateKey : undefined,
        'container',
        useMemo(
            () => ({
                switchPanel: (payload?: ResolvedContainerControlSwitchPanelParams) => {
                    if (!payload) {
                        return
                    }

                    const { switchPanelId, autoForwardLoop, loopInterval = 1000, nthPanel } = payload
                    // 仅当明确要求开启自动循环时才配置
                    if (autoForwardLoop && CONTAINER_PANEL_ACTION_TYPES.includes(switchPanelId)) {
                        startAutoLoop(switchPanelId, loopInterval, panelId => handleGlobalViewChange(panelId, undefined))
                    } else {
                        handleGlobalViewChange(switchPanelId, nthPanel)
                    }
                },
                open: () => {
                    const newBlockData = { ...blockData, config: { ...config, shown: true } }
                    onBlockChange?.(newBlockData, blockData)
                },
                close: () => {
                    const newBlockData = { ...blockData, config: { ...config, shown: false } }
                    onBlockChange?.(newBlockData, blockData)
                }
            }),
            [blockData, config, handleGlobalViewChange, onBlockChange, startAutoLoop]
        )
    )

    useMount(() => {
        const actorEvent = eventList?.find(ev => ev.type === EVENT_TYPE.LOAD)

        handleActionTriggerWithLoading({
            id: uniqueId,
            actorEvent,
            trigger: (id, actorEvent) => handleActionListTrigger(id, actorEvent, { viewRecord: record })
        })

        const anchorId = location.hash
        if (anchorId) {
            const anchorElement = document.getElementById(anchorId)
            if (anchorElement) {
                anchorElement.scrollIntoView({ behavior: 'smooth' })
            }
        }
    })

    const floatBlockList = useMemo(() => {
        const getContainerState = (containerId: string) => (containerId === baseStateKey ? containerState : undefined) || undefined
        const allFloatBlocks = getCurrentBlockChildren(blockData, {
            getContainerState,
            uniqueContainerId: baseStateKey,
            currentViewId: activeView
        })?.filter(v => v.type === BlockType.floatBox) as FloatBlockAbstract[]
        return reduce<string, FloatBlockAbstract[]>(
            (pre, id) => {
                const block = allFloatBlocks?.find(v => v.id === id)
                return block ? [...pre, block] : pre
            },
            [],
            floatPointerList ?? []
        )
    }, [blockData, floatPointerList, baseStateKey, containerState, activeView])

    const provideValue = useFloatBlock(floatBlockList, props.node.isRootSyncNode ? props.node.id : scope)

    const clickEvent = useMemo(() => eventList?.find(ev => ev.type === EVENT_TYPE.CLICK), [eventList])

    const handleClickAction = useCallback(
        (ev: React.MouseEvent | React.TouchEvent) => {
            if (!clickEvent) {
                return
            }

            if (!checkActionEvent(ev)) {
                return
            }
            return handleActionTriggerWithLoading({
                id: uniqueId,
                actorEvent: clickEvent,
                trigger: (id, actorEvent) => handleActionListTrigger(id, actorEvent, { viewRecord: record })
            })
        },
        [clickEvent, handleActionListTrigger, handleActionTriggerWithLoading, record, uniqueId]
    )

    const unifiedTap = useUnifiedTap(handleClickAction)

    const onMouseEnterAction = useCallback(
        (ev: React.MouseEvent) => {
            const actorEvent = eventList?.find(ev => ev.type === EVENT_TYPE.MOUSE_ENTER)
            if (!actorEvent) {
                return
            }
            handleActionListTrigger(uniqueId, actorEvent, { viewRecord: record })
        },
        [eventList, handleActionListTrigger, record, uniqueId]
    )

    const onMouseLeaveAction = useCallback(
        (ev: React.MouseEvent) => {
            const actorEvent = eventList?.find(ev => ev.type === EVENT_TYPE.MOUSE_LEAVE)
            if (!actorEvent) {
                return
            }
            handleActionListTrigger(uniqueId, actorEvent, { viewRecord: record })
        },
        [eventList, handleActionListTrigger, record, uniqueId]
    )

    const hasInteraction = useMemo(() => hasBlockInteraction({ hasAction: !!clickEvent, floatBlockList }), [clickEvent, floatBlockList])

    const { appId = '' } = useApplicationContext()

    const cursorValue = useMemo(() => {
        const isModify = cursor?.enable && cursor?.value === 'auto'
        if (isModify) {
            return hasInteraction ? 'pointer' : 'auto'
        }

        return getCursorStyle(appId, cursor).cursor
    }, [appId, cursor, hasInteraction])

    return (
        <FloatingTree>
            <MultipleFloatBoxProvider {...provideValue}>
                <ContainerBlock
                    {...unifiedTap}
                    onMouseEnter={onMouseEnterAction}
                    onMouseLeave={onMouseLeaveAction}
                    data-stop-action-propagation={hasInteraction ? true : undefined}
                    shown={shown}
                    style={{ ...style, cursor: cursorValue }}
                    currentView={activeView}
                    {...props}
                    ref={provideValue.targetRef}
                />
            </MultipleFloatBoxProvider>
        </FloatingTree>
    )
}

export default ContainerController
